import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField,
} from "@mui/material";
import { getCourseById, saveCourseVoice } from "../service/service";
import { getCourseVoiceEntries } from "../utils/courseVoices";

// Newly opened question forms inherit the last explicit choice on this page.
// Existing forms keep their own actor when another selector changes.
const pageSelections = new Map();

const VoiceActorSelector = ({
  courseId, course, value, onChange, onCourseChange,
  disabled = false, allowLegacyPreview = false,
}) => {
  const [loaded, setLoaded] = useState(course || null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!course);
  const pageKey = `${window.location.pathname}:${courseId}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCourseById(courseId);
      setLoaded(result.data);
      setError("");
    } catch {
      setError("Could not load voice actors.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (course) setLoaded(course);
    else refresh();
  }, [course, refresh]);

  const voices = getCourseVoiceEntries(loaded || {}).map(([voice]) => voice);
  const voiceKey = JSON.stringify(voices);
  useEffect(() => {
    const names = JSON.parse(voiceKey);
    if (loading) return;
    if (!value && names.includes(pageSelections.get(pageKey))) {
      onChange(pageSelections.get(pageKey));
    } else if (!value && names.length === 1) {
      onChange(names[0]);
    } else if (value && value !== "__legacy__" && !names.includes(value)) {
      onChange("");
    }
  }, [voiceKey, value, onChange, loading, pageKey]);

  const select = voice => {
    onChange(voice);
    if (voice && voice !== "__legacy__") pageSelections.set(pageKey, voice);
  };

  useEffect(() => {
    const registered = event => {
      if (event.detail.courseId !== courseId) return;
      const { previousName, voiceName } = event.detail;
      if (previousName && voiceName && previousName !== voiceName) {
        if (pageSelections.get(pageKey) === previousName) pageSelections.set(pageKey, voiceName);
        if (value === previousName) onChange(voiceName);
      }
      const updated = {
        ...(course || loaded),
        voices: event.detail.course.voices,
        voiceCharacters: event.detail.course.voiceCharacters,
        defaultColor: event.detail.course.defaultColor,
      };
      setLoaded(updated);
      onCourseChange?.(updated);
    };
    window.addEventListener("course-voice-registered", registered);
    return () => window.removeEventListener("course-voice-registered", registered);
  }, [courseId, course, loaded, onCourseChange, value, onChange, pageKey]);

  const add = async () => {
    if (!name.trim()) {
      setError("Enter a voice actor name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await saveCourseVoice(courseId, name.trim(), new FormData());
      window.dispatchEvent(new CustomEvent("course-voice-registered", {
        detail: { courseId, course: result.course },
      }));
      const normalized = name.trim().replace(/\s+/g, " ").toLowerCase();
      select(result.course.voices.find(voice => voice.toLowerCase() === normalized));
      setName("");
      setOpen(false);
    } catch (requestError) {
      setError(requestError.response?.data?.message ||
        requestError.response?.data?.err || "Could not add voice actor.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}>
        <TextField select label="Voice actor" size="small"
          value={voices.includes(value) || value === "__legacy__" ? value : ""}
          disabled={disabled || loading} sx={{ minWidth: 240 }}
          onChange={event => select(event.target.value)}
          SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
          <MenuItem value="" disabled>Choose a voice actor</MenuItem>
          {allowLegacyPreview && (
            <MenuItem value="__legacy__">Legacy fallback (preview only)</MenuItem>
          )}
          {voices.map(voice => <MenuItem key={voice} value={voice}>{voice}</MenuItem>)}
        </TextField>
        <Button disabled={disabled || loading}
          onClick={() => { setError(""); setOpen(true); }}>
          Add voice actor
        </Button>
      </Stack>
      {error && !open && (
        <Alert severity="error">{error}<Button onClick={refresh}>Retry</Button></Alert>
      )}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add voice actor</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Voice actor name"
            value={name} onChange={event => setName(event.target.value)}
            helperText="Character artwork is optional and can be assigned later." />
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={saving} onClick={add}>Add voice actor</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default VoiceActorSelector;
